/**
 * Vetting Service
 * Provides RPC functions for interacting with the vetting system
 */

import { supabase } from '../utils/supabaseClient';
import { type VettingStatus, type VettingNote } from '../types/vetting';
import { isRecord } from '../utils/typeGuards';

/**
 * Interface for vetting service responses
 */
export interface VettingResponse {
  success: boolean;
  data?: {
    program_id: string;
    vetting_status?: VettingStatus;
    last_action?: string;
    last_updated?: number;
    recommendation?: string;
    timestamp?: number;
  };
  error?: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as Record<string, unknown>).message === 'string') {
    return (error as { message: string }).message;
  }
  return fallback;
};

/**
 * Service for interacting with the vetting system
 */
export class VettingService {
  private missingRpcWarned = false;

  /**
   * Fetch vetting record for a program (status + notes)
   */
  async getVetting(programId: string): Promise<{ status?: VettingStatus; notes: VettingNote[]; updated_at?: string; error?: string }> {
    try {
      const { data, error } = await supabase
        .schema('prod')
        .from('vetting')
        .select('vetting_status, vetting_notes, updated_at')
        .eq('program_id', programId)
        .single();

      if (error) {
        return { notes: [], error: error.message };
      }
      const status = isRecord(data) && typeof data.vetting_status === 'string'
        ? (data.vetting_status as VettingStatus)
        : undefined;
      const notes = this.parseVettingNotes(isRecord(data) ? data.vetting_notes : undefined);
      const updatedAt = isRecord(data) && typeof data.updated_at === 'string'
        ? data.updated_at
        : undefined;
      return { status, notes, updated_at: updatedAt };
    } catch (e) {
      return { notes: [], error: getErrorMessage(e, 'Unknown error') };
    }
  }

  /**
   * Set vetting status with human action
   */
  async setVettingStatus(
    programId: string,
    newStatus: VettingStatus,
    userAction: string,
    userId: string = 'system'
  ): Promise<VettingResponse> {
    // Try to pick up the authenticated user id if available
    try {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData?.user?.id;
      if (uid) userId = uid;
    } catch {
      // ignore auth lookup errors, fall back to provided/default userId
    }

    // First attempt: prefer RPC in prod schema
    try {
      const { data, error } = await supabase
        .schema('prod')
        .rpc('vet_set_status', {
          program_id: programId,
          new_status: newStatus,
          user_action: userAction,
          user_id: userId,
        });

      if (!error && data) {
        return data as VettingResponse;
      }

      const message = error?.message ?? '';
      if (!this.missingRpcWarned && message.includes('Could not find the function prod.vet_set_status')) {
        this.missingRpcWarned = true;
        console.warn(
          'vet_set_status RPC is unavailable; falling back to direct update. Run migration 20250213100000_create_vet_set_status_function.sql to install it.'
        );
      } else {
        console.warn('vet_set_status RPC failed; attempting direct update fallback:', message);
      }
      return await this.setVettingStatusDirect(programId, newStatus, userAction, userId);
    } catch (err) {
      console.error('Exception in vet_set_status RPC; attempting direct fallback:', err);
      return await this.setVettingStatusDirect(programId, newStatus, userAction, userId);
    }
  }

  private async setVettingStatusDirect(
    programId: string,
    newStatus: VettingStatus,
    userAction: string,
    userId: string
  ): Promise<VettingResponse> {
    try {
      // Read current record to capture previous status and notes
      const { data: current, error: readErr } = await supabase
        .schema('prod')
        .from('vetting')
        .select('id, vetting_status, vetting_notes')
        .eq('program_id', programId)
        .single();

      if (readErr) {
        return { success: false, error: readErr.message };
      }

      const nowTs = Math.floor(Date.now() / 1000);
      const currentRecord = current && isRecord(current) ? current : {} as Record<string, unknown>;
      const existingNotes = this.parseVettingNotes(currentRecord['vetting_notes']);
      const nextNotes = [
        ...existingNotes,
        {
          timestamp: nowTs,
          action_type: 'human_status_change',
          old_status: (currentRecord['vetting_status'] as VettingStatus | undefined) ?? 'unknown',
          new_status: newStatus,
          user_action: userAction,
          user_id: userId,
        },
      ];

      const { data: upd, error: updErr } = await supabase
        .schema('prod')
        .from('vetting')
        .update({ vetting_status: newStatus, vetting_notes: nextNotes })
        .eq('program_id', programId)
        .select('program_id, vetting_status')
        .single();

      if (updErr) {
        return { success: false, error: updErr.message };
      }

      // Add activity logging
      try {
        await supabase
          .schema('prod')
          .from('activity_logs')
          .insert({
            program_id: programId,
            action: 'vetting_status_change',
            details: {
              old_status: (currentRecord['vetting_status'] as VettingStatus | undefined) ?? 'unknown',
              new_status: newStatus,
              user_action: userAction,
              user_id: userId,
              timestamp: nowTs,
            },
            timestamp: new Date().toISOString(),
            success: true,
          });
      } catch (logErr) {
        // Don't fail the main operation if logging fails
        console.warn('Failed to log vetting activity:', logErr);
      }

      return {
        success: true,
        data: {
          program_id: isRecord(upd) && typeof upd.program_id === 'string' ? upd.program_id : programId,
          vetting_status: isRecord(upd) && typeof upd.vetting_status === 'string'
            ? (upd.vetting_status as VettingStatus)
            : newStatus,
          last_action: userAction,
          last_updated: nowTs,
        },
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  }

  /**
   * Add AI recommendation to vetting notes
   */
  async addAIRecommendation(
    programId: string,
    aiRecommendation: string,
    confidenceScore?: number,
    aiModel: string = 'claude-3.7-sonnet'
  ): Promise<VettingResponse> {
    // First attempt RPC
    try {
      const { data, error } = await supabase
        .schema('prod')
        .rpc('vet_ai_recommend', {
          program_id: programId,
          ai_recommendation: aiRecommendation,
          confidence_score: confidenceScore,
          ai_model: aiModel,
        });

      if (!error) {
        return data as VettingResponse;
      }

      console.warn('vet_ai_recommend RPC failed; attempting direct append fallback:', error?.message);
    } catch (err) {
      console.warn('Exception in vet_ai_recommend RPC; attempting direct append fallback:', err);
    }

    // Fallback: append recommendation to notes directly
    try {
      const { data: current, error: readErr } = await supabase
        .schema('prod')
        .from('vetting')
        .select('id, vetting_notes')
        .eq('program_id', programId)
        .single();

      if (readErr) {
        return { success: false, error: readErr.message };
      }

      const nowTs = Math.floor(Date.now() / 1000);
      const currentRecord = current && isRecord(current) ? current : {} as Record<string, unknown>;
      const existingNotes = this.parseVettingNotes(currentRecord['vetting_notes']);
      const nextNotes = [
        ...existingNotes,
        {
          timestamp: nowTs,
          action_type: 'ai_recommendation',
          recommendation: aiRecommendation,
          confidence_score: confidenceScore,
          ai_model: aiModel,
        },
      ];

      const { data: upd, error: updErr } = await supabase
        .schema('prod')
        .from('vetting')
        .update({ vetting_notes: nextNotes })
        .eq('program_id', programId)
        .select('program_id')
        .single();

      if (updErr) {
        return { success: false, error: updErr.message };
      }

      // Add activity logging for AI recommendations
      try {
        await supabase
          .schema('prod')
          .from('activity_logs')
          .insert({
            program_id: programId,
            action: 'vetting_ai_recommendation',
            details: {
              recommendation: aiRecommendation,
              confidence_score: confidenceScore,
              ai_model: aiModel,
              timestamp: nowTs,
            },
            timestamp: new Date().toISOString(),
            success: true,
          });
      } catch (logErr) {
        // Don't fail the main operation if logging fails
        console.warn('Failed to log AI recommendation activity:', logErr);
      }

      return {
        success: true,
        data: {
          program_id: isRecord(upd) && typeof upd.program_id === 'string' ? upd.program_id : programId,
          recommendation: aiRecommendation,
          timestamp: nowTs,
        },
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  }

  async recordGenreCheck(
    programId: string,
    reportId: string,
    label: string,
    userId: string = 'system'
  ): Promise<VettingResponse> {
    try {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData?.user?.id;
      if (uid) userId = uid;
    } catch {
      // ignore auth lookup issues
    }

    try {
      const { data: current, error: readErr } = await supabase
        .schema('prod')
        .from('vetting')
        .select('vetting_status, vetting_notes')
        .eq('program_id', programId)
        .maybeSingle();

      if (readErr) {
        return { success: false, error: readErr.message };
      }

      const existingNotes = this.parseVettingNotes(current?.vetting_notes);
      const noteKey = `genre_check:${reportId}`;
      const filteredNotes = existingNotes.filter(
        (note) => !(note.action_type === 'genre_check' && note.user_action === noteKey)
      );

      const now = new Date();
      const nowTs = Math.floor(now.getTime() / 1000);
      const dateLabel = now.toISOString().slice(0, 10);
      const message = `Ok for ${label} - checked on ${dateLabel}`;

      const nextNotes: VettingNote[] = [
        ...filteredNotes,
        {
          timestamp: nowTs,
          action_type: 'genre_check',
          user_action: noteKey,
          user_id: userId,
          recommendation: message,
        },
      ];

      if (current) {
        const { error: updErr } = await supabase
          .schema('prod')
          .from('vetting')
          .update({ vetting_notes: nextNotes })
          .eq('program_id', programId);

        if (updErr) {
          return { success: false, error: updErr.message };
        }
      } else {
        const { error: insErr } = await supabase
          .schema('prod')
          .from('vetting')
          .insert({ program_id: programId, vetting_status: null, vetting_notes: nextNotes });

        if (insErr) {
          return { success: false, error: insErr.message };
        }
      }

      try {
        await supabase
          .schema('prod')
          .from('activity_logs')
          .insert({
            program_id: programId,
            action: 'genre_check',
            details: {
              report_id: reportId,
              label,
              user_id: userId,
              timestamp: nowTs,
            },
            timestamp: now.toISOString(),
            success: true,
          });
      } catch (logErr) {
        console.warn('Failed to log genre check activity:', logErr);
      }

      return {
        success: true,
        data: {
          program_id: programId,
          last_action: noteKey,
          last_updated: nowTs,
        },
      } satisfies VettingResponse;
    } catch (error) {
      return { success: false, error: getErrorMessage(error, 'Unknown error') };
    }
  }

  /**
   * Get vetting notes for a program
   * This is a utility function to parse the vetting_notes JSONB field
   */
  parseVettingNotes(vettingNotesRaw: unknown): VettingNote[] {
    if (!vettingNotesRaw) return [];

    try {
      // If it's already parsed as an object
      if (typeof vettingNotesRaw === 'object') {
        if (Array.isArray(vettingNotesRaw)) {
          return vettingNotesRaw as VettingNote[];
        }
        // Single note object
        return [vettingNotesRaw as VettingNote];
      }

      // If it's a string, parse it
      if (typeof vettingNotesRaw === 'string') {
        const parsed = JSON.parse(vettingNotesRaw);
        if (Array.isArray(parsed)) {
          return parsed as VettingNote[];
        }
        return [parsed as VettingNote];
      }

      console.warn('Unexpected vetting notes format:', vettingNotesRaw);
      return [];
    } catch (err) {
      console.error('Failed to parse vetting notes:', err);
      return [];
    }
  }
}

/**
 * Singleton instance for use throughout the application
 */
export const vettingService = new VettingService();

export default VettingService;
